<template>
  <div class="scheduling-calendar-setup">
    <div v-if="loadingIntegrations" class="scheduling-calendar-setup__loading">
      <LoadingSpinner size="sm" />
    </div>

    <template v-else>
      <!-- Step: add a new calendar (Google or Outlook) -->
      <template v-if="step === 'add'">
        <TextInput
          id="scheduling-setup-integration-name"
          :label="t('scheduling.setup_integration_name')"
          v-model="newIntegrationName"
          :placeholder="t('scheduling.setup_integration_name_placeholder')"
        />
        <p class="scheduling-calendar-setup__hint">{{ t('scheduling.setup_choose_provider') }}</p>
        <div class="scheduling-calendar-setup__choices">
          <button
            v-for="provider in providers"
            :key="provider.type"
            type="button"
            class="scheduling-calendar-setup__choice"
            :disabled="connectingNew"
            @click="connectProvider(provider.type)"
          >
            <img
              :src="getIntegrationIcon(provider.type)"
              alt=""
              class="scheduling-calendar-setup__choice-icon"
            />
            <span class="scheduling-calendar-setup__choice-label">{{ provider.label }}</span>
          </button>
        </div>
        <div v-if="connectingNew" class="scheduling-calendar-setup__loading-inline">
          <LoadingSpinner size="sm" />
        </div>
      </template>

      <!-- Step: select an existing calendar integration -->
      <template v-else>
        <p v-if="orgCalendarIntegrations.length === 0" class="scheduling-calendar-setup__empty">
          {{ t('scheduling.setup_no_integrations') }}
        </p>

        <div v-else class="scheduling-calendar-setup__choices">
          <button
            v-for="integration in orgCalendarIntegrations"
            :key="integration.id"
            type="button"
            class="scheduling-calendar-setup__choice"
            :class="{
              'scheduling-calendar-setup__choice--selected':
                selectedIntegrationId === integration.id,
            }"
            @click="onIntegrationChange(integration.id)"
          >
            <img
              :src="getIntegrationIcon(integration.type)"
              alt=""
              class="scheduling-calendar-setup__choice-icon"
              @error="handleIconError"
            />
            <span class="scheduling-calendar-setup__choice-label">
              {{ integration.name || integration.id }}
            </span>
          </button>
        </div>

        <button type="button" class="scheduling-calendar-setup__link" @click="startAddCalendar">
          {{ t('scheduling.setup_add_calendar_link') }}
        </button>

        <template v-if="selectedIntegrationId">
          <p v-if="integrationNeedsConnect" class="scheduling-calendar-setup__hint">
            {{ t('scheduling.setup_not_connected') }}
          </p>
          <button
            v-if="integrationNeedsConnect && canOpenIntegrationSettings"
            type="button"
            class="scheduling-calendar-setup__link"
            @click="openIntegrationSettings"
          >
            {{ t('scheduling.setup_open_integration_settings') }}
          </button>

          <div v-else-if="!integrationNeedsConnect" class="scheduling-calendar-setup__calendars">
            <div v-if="loadingCalendars" class="scheduling-calendar-setup__loading-inline">
              <LoadingSpinner size="sm" />
              <span>{{ t('scheduling.setup_loading_calendars') }}</span>
            </div>
            <SelectInput
              v-else
              id="scheduling-setup-calendar"
              :label="t('scheduling.setup_select_calendar')"
              :options="calendarOptions"
              :modelValue="selectedCalendarId"
              @update:modelValue="selectedCalendarId = $event"
              :placeholder="t('scheduling.setup_select_calendar_placeholder')"
            />
            <p v-if="calendarLoadError" class="scheduling-calendar-setup__error" role="alert">
              {{ calendarLoadError }}
            </p>
            <button
              v-if="calendarLoadError && canOpenIntegrationSettings"
              type="button"
              class="scheduling-calendar-setup__link"
              @click="openIntegrationSettings"
            >
              {{ t('scheduling.setup_open_integration_settings') }}
            </button>
          </div>
        </template>
      </template>

      <p v-if="formError" class="scheduling-calendar-setup__error" role="alert">
        {{ formError }}
      </p>

      <div class="scheduling-calendar-setup__actions">
        <Btn
          v-if="step === 'select'"
          variant="primary"
          type="button"
          :loading="saving"
          :disabled="!canSave || saving"
          @click="handleSave"
        >
          {{ t('scheduling.setup_save') }}
        </Btn>
        <Btn v-else variant="outlined" type="button" :disabled="connectingNew" @click="goBack">
          {{ t('scheduling.setup_back') }}
        </Btn>
      </div>
    </template>
  </div>
</template>

<script>
  import { computed, onMounted, ref, watch } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import IntegrationService from '@/services/integrationService';
  import IntegrationSetupModal from '@/components/integrations/IntegrationSetupModal.vue';
  import GoogleCalendarService from '@/services/calendarService';
  import { useFormStore } from '@/store/formStore';
  import { useIntegrationStore } from '@/store/integrationStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { useI18n } from '@/composables/useI18n';

  export default {
    name: 'SchedulingCalendarSetupModal',
    components: {
      Btn,
      LoadingSpinner,
      SelectInput,
      TextInput,
    },
    props: {
      onComplete: {
        type: Function,
        default: null,
      },
      onClose: {
        type: Function,
        default: null,
      },
      modalId: {
        type: Number,
        default: null,
      },
    },
    setup(props) {
      const { t } = useI18n();
      const formStore = useFormStore();
      const integrationStore = useIntegrationStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();

      const formId = computed(() => formStore.currentForm?.id);

      const CALENDAR_TYPES = ['google_calendar', 'outlook_calendar'];
      const providers = [
        { type: 'google_calendar', label: 'Google Calendar' },
        { type: 'outlook_calendar', label: 'Outlook' },
      ];

      const step = ref('select');
      const newIntegrationName = ref('');
      const connectingNew = ref(false);
      let knownIntegrationIds = new Set();

      const loadingIntegrations = ref(true);
      const loadingCalendars = ref(false);
      const saving = ref(false);
      const formError = ref('');
      const calendarLoadError = ref('');

      const selectedIntegrationId = ref('');
      const selectedCalendarId = ref('');
      const calendars = ref([]);
      const hydratedIntegration = ref(null);

      const orgCalendarIntegrations = computed(() =>
        (integrationStore.integrations || []).filter(
          (item) => CALENDAR_TYPES.includes(item.type) && item.is_active
        )
      );

      const calendarOptions = computed(() =>
        calendars.value.map((calendar) => ({
          label: calendar.summary || calendar.id,
          value: calendar.id,
        }))
      );

      const integrationNeedsConnect = computed(() => {
        if (!hydratedIntegration.value) return false;
        const settings = hydratedIntegration.value.settings || {};
        return !settings.refresh_token && !settings.access_token;
      });

      // The settings modal only knows how to reconnect Google integrations
      const canOpenIntegrationSettings = computed(
        () => hydratedIntegration.value?.type === 'google_calendar'
      );

      const canSave = computed(() => {
        if (!selectedIntegrationId.value || integrationNeedsConnect.value) return false;
        if (!selectedCalendarId.value) return false;
        return true;
      });

      async function loadIntegrations() {
        loadingIntegrations.value = true;
        try {
          await integrationStore.fetchIntegrations();
          if (!selectedIntegrationId.value && orgCalendarIntegrations.value.length === 1) {
            selectedIntegrationId.value = orgCalendarIntegrations.value[0].id;
          }
        } catch (error) {
          console.error('Failed to load integrations:', error);
          formError.value = t('scheduling.setup_load_integrations_failed');
        } finally {
          loadingIntegrations.value = false;
        }
      }

      async function loadCalendarsForIntegration(integrationId) {
        calendarLoadError.value = '';
        calendars.value = [];
        selectedCalendarId.value = '';
        hydratedIntegration.value = null;

        if (!integrationId) return;

        loadingCalendars.value = true;
        try {
          const integration = await integrationStore.ensureIntegrationDetails(integrationId);
          hydratedIntegration.value = integration;

          if (!integration?.settings?.refresh_token) {
            return;
          }

          const list =
            integration.type === 'outlook_calendar'
              ? await GoogleCalendarService.listOutlookCalendars(integration)
              : await GoogleCalendarService.listCalendars(integration);
          calendars.value = Array.isArray(list) ? list : [];

          const existingId = integration.settings?.calendar_id;
          if (existingId && calendars.value.some((cal) => cal.id === existingId)) {
            selectedCalendarId.value = existingId;
          } else if (calendars.value.length === 1) {
            selectedCalendarId.value = calendars.value[0].id;
          }
        } catch (error) {
          console.error('Failed to load calendars:', error);
          calendarLoadError.value = t('scheduling.setup_load_calendars_failed');
        } finally {
          loadingCalendars.value = false;
        }
      }

      function getIntegrationIcon(type) {
        // outlook_calendar reuses outlook.png (no file named after the type)
        const iconName = type === 'outlook_calendar' ? 'outlook' : type;
        return new URL(`../../../assets/img/integrations/${iconName}.png`, import.meta.url).href;
      }

      function handleIconError(event) {
        event.target.src = new URL(
          '../../../assets/img/integrations/webhook.png',
          import.meta.url
        ).href;
      }

      function onIntegrationChange(integrationId) {
        selectedIntegrationId.value = integrationId;
      }

      watch(orgCalendarIntegrations, (list) => {
        if (!selectedIntegrationId.value && list.length === 1) {
          selectedIntegrationId.value = list[0].id;
        }
      });

      watch(selectedIntegrationId, (integrationId) => {
        loadCalendarsForIntegration(integrationId);
      });

      function openIntegrationSettings() {
        if (!hydratedIntegration.value) return;
        const integration = hydratedIntegration.value;
        const setupModalId = uiStore.addModal(IntegrationSetupModal, {
          title: t('scheduling.setup_edit_integration_title', { name: integration.name }),
          description: t('scheduling.setup_edit_integration_description'),
          integration,
          isEdit: true,
          onSubmit: async (formData) => {
            await integrationStore.updateIntegration(integration.id, formData);
            await integrationStore.fetchIntegrations();
            await loadCalendarsForIntegration(integration.id);
            uiStore.closeModal(setupModalId);
          },
        });
      }

      function startAddCalendar() {
        formError.value = '';
        newIntegrationName.value = '';
        step.value = 'add';
      }

      function goBack() {
        formError.value = '';
        step.value = 'select';
      }

      // Wait for the OAuth popup to close; the page itself stays alive
      function waitForPopupClose(popup) {
        return new Promise((resolve) => {
          const timer = setInterval(() => {
            if (popup.closed) {
              clearInterval(timer);
              resolve();
            }
          }, 500);
        });
      }

      async function connectProvider(type) {
        formError.value = '';
        connectingNew.value = true;
        const knownIds = new Set((integrationStore.integrations || []).map((item) => item.id));
        const name = newIntegrationName.value.trim();
        try {
          const response =
            type === 'outlook_calendar'
              ? await IntegrationService.connectOutlookCalendar(name, { popup: true })
              : await IntegrationService.connectGoogleCalendar(name);
          if (!response?.url) throw new Error('No OAuth URL returned');

          const popup = window.open(
            response.url,
            `${type}-auth`,
            'width=620,height=760,resizable=yes,scrollbars=yes'
          );
          if (!popup) {
            formError.value = t('scheduling.setup_popup_blocked');
            return;
          }
          await waitForPopupClose(popup);
          await handleNewIntegrationConnected(type, knownIds);
        } catch (error) {
          console.error('Failed to start calendar connection:', error);
          formError.value =
            error?.response?.data?.code === 'OUTLOOK_NOT_CONFIGURED'
              ? t('scheduling.setup_outlook_unavailable')
              : t('scheduling.setup_connect_failed');
        } finally {
          connectingNew.value = false;
        }
      }

      // The OAuth callback creates the integration itself, so find it by diffing the list.
      async function handleNewIntegrationConnected(type, knownIds) {
        await integrationStore.fetchIntegrations();
        const created = (integrationStore.integrations || []).find(
          (item) => item.type === type && !knownIds.has(item.id)
        );
        if (!created) {
          formError.value = t('scheduling.setup_connect_failed');
          return;
        }
        if (formId.value) {
          await integrationStore.toggleIntegration(formId.value, created.id, true);
        }
        selectedIntegrationId.value = created.id;
        toastStore.addToast({
          type: 'success',
          message: t('scheduling.setup_connect_success', { name: created.name }),
        });
        step.value = 'select';
      }

      async function syncFormEnabledIntegration(integrationId) {
        const formId = formStore.currentForm?.id;
        if (!formId) return;

        const isEnabled = integrationStore.isIntegrationEnabled(formId, integrationId);
        if (!isEnabled) {
          await integrationStore.toggleIntegration(formId, integrationId, true);
        }

        const hydrated = await integrationStore.ensureIntegrationDetails(integrationId, {
          force: true,
        });
        if (!hydrated) return;

        const current = formStore.currentForm.enabled_integrations || [];
        const next = current.filter((item) => item.id !== integrationId);
        formStore.currentForm.enabled_integrations = [...next, hydrated];
      }

      async function handleSave() {
        formError.value = '';
        if (!selectedIntegrationId.value) {
          formError.value = t('scheduling.setup_integration_required');
          return;
        }
        if (!selectedCalendarId.value) {
          formError.value = t('scheduling.setup_calendar_required');
          return;
        }

        const calendar = calendars.value.find((item) => item.id === selectedCalendarId.value);
        const integration = hydratedIntegration.value;
        if (!integration) {
          formError.value = t('scheduling.setup_integration_required');
          return;
        }

        saving.value = true;
        try {
          await integrationStore.updateIntegration(integration.id, {
            name: integration.name,
            type: integration.type,
            is_active: integration.is_active,
            settings: {
              ...(integration.settings || {}),
              calendar_id: selectedCalendarId.value,
              calendar_name: calendar?.summary || calendar?.id || selectedCalendarId.value,
            },
          });

          await syncFormEnabledIntegration(integration.id);

          if (typeof props.onComplete === 'function') {
            props.onComplete({ integrationId: integration.id, integrationName: integration.name });
          }
        } catch (error) {
          console.error('Failed to save scheduling calendar setup:', error);
          formError.value = t('scheduling.setup_save_failed');
        } finally {
          saving.value = false;
        }
      }

      onMounted(async () => {
        await loadIntegrations();
      });

      return {
        t,
        loadingIntegrations,
        loadingCalendars,
        saving,
        formError,
        calendarLoadError,
        orgCalendarIntegrations,
        providers,
        step,
        newIntegrationName,
        connectingNew,
        startAddCalendar,
        goBack,
        connectProvider,
        calendarOptions,
        getIntegrationIcon,
        handleIconError,
        selectedIntegrationId,
        selectedCalendarId,
        integrationNeedsConnect,
        canOpenIntegrationSettings,
        canSave,
        onIntegrationChange,
        openIntegrationSettings,
        handleSave,
      };
    },
  };
</script>

<style scoped lang="scss">
  .scheduling-calendar-setup {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-width: min(100%, 28rem);

    &__loading {
      display: flex;
      justify-content: center;
      padding: 2rem 0;
    }

    &__loading-inline {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
      color: var(--slate-600);
    }

    &__intro {
      margin: 0;
      color: var(--slate-700);
      font-size: 0.9375rem;
    }

    &__choices {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.75rem;
    }

    &__choice {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      padding: 1.5rem 1rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.75rem;
      background: #fff;
      cursor: pointer;
      transition:
        border-color 0.15s ease,
        background-color 0.15s ease;

      &:hover:not(:disabled) {
        border-color: var(--brand-400);
        background: var(--brand-50, #eff6ff);
      }

      &--selected {
        border-color: var(--brand-500);
        background: var(--brand-50, #eff6ff);
        box-shadow: 0 0 0 1px var(--brand-500);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    &__choice-icon {
      width: 2.5rem;
      height: 2.5rem;
      object-fit: contain;
    }

    &__choice-label {
      font-size: 0.9375rem;
      font-weight: 500;
      color: var(--slate-800);
      text-align: center;
    }

    &__empty {
      margin: 0;
      color: var(--slate-700);
      font-size: 0.9375rem;
    }

    &__integration-field {
      margin: 0;
      padding: 0;
      border: none;
      min-width: 0;
    }

    &__integration-legend {
      padding: 0;
      margin: 0 0 0.5rem;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--slate-700);
    }

    &__integration-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(8.5rem, 1fr));
      gap: 0.75rem;
    }

    &__integration-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
      padding: 1rem 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.75rem;
      background: #fff;
      cursor: pointer;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease,
        background-color 0.15s ease;

      &:hover {
        border-color: var(--brand-400);
      }

      &--selected {
        border-color: var(--brand-500);
        background: var(--brand-50, #eff6ff);
        box-shadow: 0 0 0 1px var(--brand-500);
      }
    }

    &__integration-icon {
      width: 2.25rem;
      height: 2.25rem;
      object-fit: contain;
    }

    &__integration-name {
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--slate-800);
      text-align: center;
      word-break: break-word;
      line-height: 1.3;
    }

    &__hint {
      margin: 0;
      font-size: 0.875rem;
      color: var(--slate-600);
    }

    &__link {
      align-self: flex-start;
      padding: 0;
      border: none;
      background: none;
      color: var(--brand-600);
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      text-decoration: underline;

      &--secondary {
        margin-top: 0.25rem;
      }
    }

    &__error {
      margin: 0;
      color: var(--red-600);
      font-size: 0.875rem;
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      margin-top: 0.5rem;
      padding-top: 0.75rem;
      border-top: 1px solid var(--slate-200);
    }
  }
</style>
